import { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './useAuth'

// useEffectiveRole — single source of truth for the viewer's role in the
// context of a specific event. Mirrors the get_effective_role() Postgres
// function (see migration 20260511030000) so the client and DB agree on
// what powers the user has.
//
// Waterfall mirrors the DB:
//   1. users.platform_role NOT NULL  -> 'm55_' || platform_role
//   1b. users.role = 'm55_admin'     -> 'm55_admin' (legacy bridge)
//   2. no eventId -> 'none' (without an event we can't resolve tiers 3-5)
//   3. org_members.org_role IN (org_owner, org_admin, org_gm) -> 'gm'
//      (Bug 34: all three org-side staff roles get gm-equivalent authority
//      at the event level — matches the DB get_effective_role waterfall.)
//   4. event_players.event_role     -> that value
//   5. else                          -> 'none'
//
// Returns:
//   effectiveRole   : the raw role string (e.g. 'm55_admin', 'gm', 'co_gm',
//                     'player', 'none', 'm55_staff', 'm55_support')
//   isM55Staff      : effective role starts with 'm55_'
//   isM55Admin      : effective role === 'm55_admin'
//   isOrgGm         : the user's org_members.org_role for the event's org is 'org_gm'
//   isOrgOwnerOrAdmin: org_members.org_role IN ('org_owner','org_admin')
//   isGm            : effectiveRole === 'gm'
//   isCoGm          : effectiveRole === 'co_gm'
//   isPlayer        : effectiveRole === 'player'
//   canManageEvent  : isM55Staff || isGm  (full event-level write power)
//   canManageOrg    : isM55Staff || isOrgOwnerOrAdmin
//   factionId       : the user's event_players.faction_id
//   coGmFactionId   : the user's event_players.co_gm_faction_id (only set
//                     for co_gm roles)
//   canViewFaction(f): for faction-scoped visibility decisions
//   loading         : initial fetch in flight
//
// Back-compat aliases (so existing useEventRole consumers keep working):
//   role            : effectiveRole (or null when 'none')
//   isFullGM        : canManageEvent  (m55 staff OR direct event GM)
//   canCreate       : canManageEvent || isCoGm
export default function useEffectiveRole(eventId) {
  const { user, profile } = useAuth()
  const [eventPlayer, setEventPlayer] = useState(null)
  const [orgRole, setOrgRole] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!user || !eventId) {
        setEventPlayer(null)
        setOrgRole(null)
        setLoading(false)
        return
      }
      setLoading(true)
      // Pull the user's event_players row + the event's org_id in one round
      // trip. The nested events embed gives us org_id without a follow-up
      // query.
      const { data: epRows } = await supabase
        .from('event_players')
        .select('event_role, faction_id, co_gm_faction_id, reg_status, event:events!event_players_event_id_fkey(org_id)')
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .limit(1)
      const ep = epRows?.[0] || null

      // org_id is available even if the user isn't on the event roster, so
      // fetch it directly in that case.
      let orgId = ep?.event?.org_id || null
      if (!orgId) {
        const { data: evRows } = await supabase
          .from('events').select('org_id').eq('id', eventId).limit(1)
        orgId = evRows?.[0]?.org_id || null
      }

      let omRole = null
      if (orgId) {
        const { data: omRows } = await supabase
          .from('org_members')
          .select('org_role')
          .eq('org_id', orgId)
          .eq('user_id', user.id)
          .limit(1)
        omRole = omRows?.[0]?.org_role || null
      }

      if (cancelled) return
      setEventPlayer(ep)
      setOrgRole(omRole)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [user?.id, eventId])

  // Tier 1 — platform staff. New platform_role first, legacy users.role as
  // the fallback bridge until users.role is deprecated.
  const platformRole = profile?.platform_role || null
  const isM55Staff =
    !!platformRole || profile?.role === 'm55_admin'
  const isM55Admin = platformRole === 'admin' || profile?.role === 'm55_admin'

  // Tier 2 — org membership for the event's org.
  const isOrgGm = orgRole === 'org_gm'
  const isOrgOwnerOrAdmin = orgRole === 'org_owner' || orgRole === 'org_admin'
  // org_owner / org_admin / org_gm all collapse to gm-equivalent authority
  // for event-level RLS (matches DB get_effective_role — Bug 34).
  const hasOrgGmAuthority = isOrgGm || isOrgOwnerOrAdmin

  // Tier 3 — direct event role.
  const directEventRole = eventPlayer?.event_role || null

  // Compose the effective role through the waterfall. Mirrors the DB
  // function exactly so a client gate and a DB RLS gate agree.
  let effectiveRole = 'none'
  if (platformRole) effectiveRole = 'm55_' + platformRole
  else if (profile?.role === 'm55_admin') effectiveRole = 'm55_admin'
  else if (!eventId) effectiveRole = 'none'
  else if (hasOrgGmAuthority) effectiveRole = 'gm'
  else if (directEventRole) effectiveRole = directEventRole

  const isGm    = effectiveRole === 'gm'
  const isCoGm  = effectiveRole === 'co_gm'
  const isPlayer = effectiveRole === 'player'
  // ANU-90 — In-Game Admin. Referee tier: read-only across both factions
  // (no fog of war) + BLIND MAN trigger + war-crime acknowledgement, but
  // strictly NO event-management writes. Intentionally NOT folded into
  // isGm / isCoGm / canManageEvent.
  const isIGA   = effectiveRole === 'iga'

  const canManageEvent = isM55Staff || isGm
  const canManageOrg   = isM55Staff || isOrgOwnerOrAdmin

  const factionId = eventPlayer?.faction_id || null
  const coGmFactionId = isCoGm ? (eventPlayer?.co_gm_faction_id || null) : null

  const canViewFaction = (fid) => {
    if (canManageEvent) return true
    if (isCoGm) {
      if (!fid) return true
      return fid === coGmFactionId
    }
    return false
  }

  return {
    // New spec'd shape
    effectiveRole,
    isM55Staff,
    isM55Admin,
    isOrgGm,
    isOrgOwnerOrAdmin,
    isGm,
    isCoGm,
    isPlayer,
    isIGA,
    canManageEvent,
    canManageOrg,
    factionId,
    coGmFactionId,
    canViewFaction,
    loading,

    // Back-compat aliases used by useEventRole consumers (GameEvent etc.).
    // Same semantics as before: isFullGM was true for both m55_admin and
    // direct event_role='gm'.
    role: effectiveRole === 'none' ? null : effectiveRole,
    isFullGM: canManageEvent,
    canCreate: canManageEvent || isCoGm,
  }
}
